import type { AnchorHTMLAttributes, ReactNode } from "react";
import { ReferenceChip, type ReferenceTargetKind } from "@/components/ui/reference-chip";
import { normalizeReferenceTargetKind, useReferencePresentation } from "@/features/rich-text";
import { toInternalAppPath } from "@/lib/internal-link-label";
import { cn } from "@/lib/utils";

interface ClaudiaReferenceLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "href" | "onClick"> {
  href: string;
  children: ReactNode;
  targetKind?: ReferenceTargetKind | "folder";
  entityTypeKey?: string;
  entityId?: string;
  versionNumber?: number | null;
  onNavigate?: (path: string) => void;
}

export function getClaudiaReferenceInternalPath(href: string) {
  const internalPath = toInternalAppPath(href);
  return internalPath?.startsWith("/project/") ? internalPath : null;
}

export function ClaudiaReferenceLink({
  href,
  children,
  targetKind,
  entityTypeKey,
  entityId,
  versionNumber,
  onNavigate,
  className,
  ...props
}: ClaudiaReferenceLinkProps) {
  const internalPath = getClaudiaReferenceInternalPath(href);
  const resolvedHref = internalPath ?? href;
  const normalizedTargetKind = normalizeReferenceTargetKind(targetKind);
  const presentation = useReferencePresentation({
    kind: "link",
    targetKind: normalizedTargetKind ?? undefined,
    entityTypeKey,
    href,
  });

  return (
    <ReferenceChip
      {...props}
      as="a"
      href={resolvedHref}
      label={children}
      icon={presentation.Icon}
      interactive
      referenceKind="link"
      targetKind={normalizedTargetKind}
      className={cn("no-underline", className)}
      data-claudia-link-target={normalizedTargetKind ?? targetKind}
      data-entity-type-key={entityTypeKey}
      data-entity-id={entityId}
      data-version-number={versionNumber ?? undefined}
      data-reference-target-kind={normalizedTargetKind ?? undefined}
      onClick={(event) => {
        if (!internalPath || !onNavigate) {
          return;
        }
        event.preventDefault();
        onNavigate(internalPath);
      }}
    />
  );
}
